import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import SelectGoalTree from './SelectGoalTree/SelectGoalTree.container';
import Icon from './Icon';
import MultiValueField from './Multiselects/MultiValueField';
import { useTranslation } from './ChildrenContext';

const InputGroupMultiselect = ({
    tags: propTags,
    onChange: propOnChange,
    nesting: propNesting
}) => {
    const t = useTranslation();
    const [modalIsVisible, setModalIsVisible] = useState(false);
    const [tags, setTags] = useState(propTags);
    const [initTags] = useState(!!propTags.length);
    const tagsRef = useRef(propTags);
    const isInternalUpdate = useRef(false);

    const getTagKey = useCallback((item) => {
        if (!item || typeof item !== 'object') {
            return item === undefined || item === null ? '' : String(item);
        }

        if (item.id !== undefined && item.id !== null) {
            return `id:${item.id}`;
        }

        if (item.value !== undefined && item.value !== null) {
            return `value:${item.value}`;
        }

        if (item.title) {
            return `title:${item.title}`;
        }

        return JSON.stringify(item);
    }, []);

    useEffect(() => {
        tagsRef.current = tags;
    }, [tags]);

    const getArrayIds = useCallback((array = tagsRef.current) => {
        return array.map(item => item.id || item.value);
    }, []);

    useEffect(() => {
        if (!isInternalUpdate.current) {
            const currentIds = getArrayIds(tagsRef.current);
            const propIds = getArrayIds(propTags);
            if (JSON.stringify(currentIds) !== JSON.stringify(propIds)) {
                setTags(propTags);
                tagsRef.current = propTags;
            }
        }
        isInternalUpdate.current = false;
    }, [propTags, getArrayIds]);

    const toggleModal = useCallback((event) => {
        if (event && event.target.nodeName !== 'UL') {
            return;
        }

        setModalIsVisible(prevState => !prevState);
    }, []);

    const onInitTags = useCallback((items) => {
        setTags(items);
    }, []);

    const onChange = useCallback((newTags) => {
        let ids = getArrayIds(newTags);
        propOnChange && propOnChange(ids);
    }, [getArrayIds, propOnChange]);

    const onDeleteTag = useCallback((event) => {
        isInternalUpdate.current = true;
        let newTags = [...tagsRef.current];
        let index = event.target.getAttribute('data-index');

        newTags.splice(index, 1);

        setTags(newTags);
        tagsRef.current = newTags;
        onChange(newTags);
    }, [onChange]);

    const onAddTags = useCallback((items) => {
        if (!items || !Array.isArray(items) || items.length === 0) {
            return;
        }

        const normalizedItems = items.map(item => {
            if (!item || typeof item !== 'object') {
                return null;
            }
            if (!item.title && (item.value || item.id)) {
                return {
                    ...item,
                    title: item.title || String(item.value || item.id)
                };
            }
            return item;
        }).filter(Boolean);

        if (normalizedItems.length === 0) {
            return;
        }

        const existingKeys = new Set(tagsRef.current.map(getTagKey));
        const filteredItems = normalizedItems.filter((item) => {
            const key = getTagKey(item);
            if (!key || existingKeys.has(key)) {
                return false;
            }
            existingKeys.add(key);
            return true;
        });

        if (!filteredItems.length) {
            return;
        }

        isInternalUpdate.current = true;
        let newTags = [...tagsRef.current].concat(filteredItems);

        setTags(newTags);
        tagsRef.current = newTags;
        onChange(newTags);
    }, [onChange, getTagKey]);

    // cancel in the collapsible wrapper: put back the tags the editor was opened with
    const onRestoreTags = useCallback((snapshot) => {
        const restored = Array.isArray(snapshot) ? snapshot : [];
        isInternalUpdate.current = true;
        setTags(restored);
        tagsRef.current = restored;
        onChange(restored);
    }, [onChange]);

    const tagTitles = tags
        .map(item => item && (item.title || (item.value ? String(item.value) : (item.id ? String(item.id) : ''))))
        .filter(Boolean);

    // SelectGoalTree stays mounted outside the collapsible wrapper: it loads the titles of the initial tags
    return (
        <React.Fragment>
        <MultiValueField
            titles={tagTitles}
            value={tags}
            onRestore={onRestoreTags}
            ignoreOutsideClick={modalIsVisible}
        >
        <div className="input-group-multiselect">
            <ul
                className="input-group-multiselect-taglist"
                onClick={toggleModal}
            >
                {/* like the input of the other multiselect editors: "Enter value" above the selected tags */}
                <li className="input-group-multiselect-placeholder">{(t && t.reportPage && t.reportPage.enterValue) || ''}</li>
                {tags.map((item, i) => {
                    if (!item) return null;
                    const displayText = item.title || (item.value ? String(item.value) : (item.id ? String(item.id) : ''));
                    const itemKey = item.value || item.id || i;
                    if (!displayText) return null;
                    return (
                        <li className="rw-multiselect-tag input-group-multiselect-tag" key={itemKey}>
                            {displayText}
                            <Icon name={'fa-close input-group-multiselect-close'}
                                data-index={i}
                                onClick={onDeleteTag}
                            />
                        </li>
                    );
                })}
            </ul>
        </div>
        </MultiValueField>
            <SelectGoalTree
                initGoalActions={initTags ? {
                    ids: getArrayIds(propTags).join(','),
                    onSuccess: onInitTags
                } : null}
                modalIsVisible={modalIsVisible}
                nesting={propNesting}
                blockedIdGoalActions={getArrayIds()}
                onCloseModal={toggleModal}
                onConfirm={onAddTags}
            />
        </React.Fragment>
    );
};

InputGroupMultiselect.propTypes = {
    tags: PropTypes.array,
    onChange: PropTypes.func,
    nesting: PropTypes.number
};

InputGroupMultiselect.defaultProps = {
    tags: [],
    onChange: () => {},
    nesting: 0
};

export default InputGroupMultiselect;
